import type {
  Allarme,
  Comando,
  CredenzialiTecnico,
  Energia,
  EventoDispositivo,
  EventoStato,
  Generatore,
  Impianto,
  ImpiantoSintesi,
  Lettura,
  LetturaCanale,
  ParametriRegolazione,
  PatchCanale,
  ProgrammaOrario,
  RichiestaForzatura,
  RispostaComando,
  SessioneTecnica,
  StatoAttuatore,
  StatoImpianto,
  StatoOta,
  StatoSorgente,
  VoceRegistro,
} from '@emacomfort/domain';

export type Risoluzione = '1m' | '15m' | '1h' | '1d';

export interface RichiestaStorico {
  da: number;
  a: number;
  canali: string[];
  risoluzione: Risoluzione;
}

export interface PuntoStorico {
  ts: number;
  valori: Record<string, number | null>;
}

export interface Istantanea {
  ts: number;
  letture: Record<string, Lettura>;
  attuatori: Record<string, StatoAttuatore>;
  stato: StatoImpianto;
  energia: Record<string, Energia>;
  sorgenti: Record<string, StatoSorgente>;
  allarmi: Allarme[];
}

export type Ascoltatore = (evento: EventoDispositivo) => void;

/**
 * Strato di astrazione dispositivo (specifica tecnica §9.5). Tutte le feature
 * parlano con questa interfaccia, mai direttamente con la rete. Tre
 * implementazioni intercambiabili: Simulator, LocalHttp, Cloud.
 */
export interface DeviceClient {
  readonly impiantoId: string;

  getImpianto(): Promise<Impianto>;
  getIstantanea(): Istantanea;
  sottoscrivi(ascoltatore: Ascoltatore): () => void;

  /** Risolve con l'esito FINALE (`applicato` o `fallito`); `accettato` arriva come evento. */
  inviaComando(comando: Comando): Promise<RispostaComando>;

  getStorico(richiesta: RichiestaStorico): Promise<PuntoStorico[]>;
  getEventi(): Promise<EventoStato[]>;

  getAllarmi(): Promise<Allarme[]>;
  riconosciAllarme(id: string, chi: string): Promise<void>;

  getProgramma(zonaId: string): Promise<ProgrammaOrario>;

  readonly tecnico: AreaTecnicaClient;
}

/** Endpoint 🔒 (contratto API §10.1): autorizzazione verificata dalla centralina, non dal client (RNF-22). */
export interface AreaTecnicaClient {
  accedi(credenziali: CredenzialiTecnico): Promise<SessioneTecnica>;
  getCanali(sessione: SessioneTecnica): Promise<LetturaCanale[]>;
  patchCanale(sessione: SessioneTecnica, canaleId: string, patch: PatchCanale): Promise<void>;
  patchZonaCarico(sessione: SessioneTecnica, zonaId: string, tipoCarico: Impianto['zone'][number]['tipoCarico']): Promise<void>;
  getParametri(sessione: SessioneTecnica): Promise<ParametriRegolazione>;
  patchParametri(sessione: SessioneTecnica, parametri: ParametriRegolazione): Promise<void>;
  getGeneratori(sessione: SessioneTecnica): Promise<Generatore[]>;
  putGeneratori(sessione: SessioneTecnica, generatori: Generatore[]): Promise<void>;
  forzatura(sessione: SessioneTecnica, richiesta: RichiestaForzatura): Promise<void>;
  annullaForzatura(sessione: SessioneTecnica, canale: string): Promise<void>;
  getRegistro(sessione: SessioneTecnica): Promise<VoceRegistro[]>;
  getOta(sessione: SessioneTecnica): Promise<StatoOta>;
  avviaOta(sessione: SessioneTecnica, opzioni: { firmata: boolean }): Promise<StatoOta>;
  rollbackOta(sessione: SessioneTecnica): Promise<StatoOta>;
}

/** Parco impianti dell'installatore (T2). */
export interface ParcoClient {
  readonly tenantId: string;
  listaImpianti(): Promise<ImpiantoSintesi[]>;
  clientPer(impiantoId: string): DeviceClient;
}

export class ErroreAutorizzazione extends Error {
  constructor(message = 'non_autorizzato') {
    super(message);
    this.name = 'ErroreAutorizzazione';
  }
}

export class ErroreRichiesta extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErroreRichiesta';
  }
}
